import { useState, useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SectionHeading } from '@/components/ui/section'
import { Search, X, Minimize2, Maximize2, ArrowLeft } from 'lucide-react'
import { ConversationList } from './ConversationList'
import { ConversationPanel } from './ConversationPanel'
import { useConversationsQuery } from '@/queries/conversations/useConversationsQuery'
import { type Conversation } from '@/data/mockOrdersData'
import { cn } from '@/lib/utils'

interface InboxModalProps {
	isOpen: boolean
	onClose: () => void
	onMinimize: () => void
	onToggleMode?: () => void
	bubblePosition: { bottom: number; right: number }
	mode: 'fullscreen' | 'panel'
	className?: string
	isDragging?: boolean
	orderId?: string
	onCloseAll?: () => void
	isOrderPanelOpen?: boolean
}

export function InboxModal({
	isOpen,
	onClose,
	onMinimize: _onMinimize,
	onToggleMode,
	bubblePosition,
	mode,
	className,
	isDragging = false,
	orderId,
	onCloseAll,
	isOrderPanelOpen = false
}: InboxModalProps) {
	const [searchQuery, setSearchQuery] = useState('')
	const [view, _setView] = useState<'list' | 'grid'>('list')
	const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null)
	const [showConversationsList, setShowConversationsList] = useState(false)
	const [isAnimatingIn, setIsAnimatingIn] = useState(false)
	const [isAnimatingOut, setIsAnimatingOut] = useState(false)
	const [shouldRender, setShouldRender] = useState(false)
	const [modalPosition, setModalPosition] = useState(bubblePosition)
	

	// Option 2: Modal stays in place during close animation
	useEffect(() => {
		if (!isAnimatingOut && !isAnimatingIn) {
			// Only update position when not animating (keeps modal in place during close)
			setModalPosition(bubblePosition)
		}
	}, [bubblePosition, isAnimatingOut, isAnimatingIn])
	const [windowWidth, setWindowWidth] = useState(window.innerWidth)
	const modalRef = useRef<HTMLDivElement>(null)

	const queryFilters = orderId ? { orderId } : (searchQuery ? { search: searchQuery } : undefined)

	const { data: conversationsData } = useConversationsQuery({
		filters: queryFilters
	})
	

	const conversations = conversationsData?.conversations || []

	// Auto-select oldest unread conversation
	useEffect(() => {
		if (conversations.length > 0 && !selectedConversation) {
			if (mode === 'fullscreen') {
				// Fullscreen: auto-select oldest unread or most recent
				const unreadConversations = conversations.filter(c => c.unread_count > 0)
				if (unreadConversations.length > 0) {
					const oldestUnread = unreadConversations.sort((a, b) => 
						new Date(a.last_message_at).getTime() - new Date(b.last_message_at).getTime()
					)[0]
					setSelectedConversation(oldestUnread)
				} else {
					setSelectedConversation(conversations[0])
				}
				setShowConversationsList(false)
			} else {
				// Panel: if we have an orderId, auto-select the conversation; otherwise show list
				if (orderId) {
					// When orderId is provided, auto-select the first conversation
					setSelectedConversation(conversations[0])
					setShowConversationsList(false)
				} else {
					// No orderId: show conversations list by default
					setShowConversationsList(true)
				}
			}
		}
	}, [conversations, selectedConversation, mode, orderId])

	// Handle opening and closing animations
	useEffect(() => {
		
		if (isOpen) {
			// Opening: render modal and start animation
			setShouldRender(true)
			setIsAnimatingOut(false)
			setIsAnimatingIn(false)
			// Trigger opening animation after a brief delay
			const timer = setTimeout(() => setIsAnimatingIn(true), 50)
			return () => clearTimeout(timer)
		} else if (shouldRender) {
			// Closing: start closing animation
			setIsAnimatingOut(true)
			setIsAnimatingIn(false)
			// Remove modal after animation completes
			const timer = setTimeout(() => {
				setShouldRender(false)
				setIsAnimatingOut(false)
			}, 300)
			return () => clearTimeout(timer)
		}
	}, [isOpen, shouldRender])

	// Handle window resize to keep modal positioned correctly
	useEffect(() => {
		const handleResize = () => {
			setWindowWidth(window.innerWidth)
		}

		window.addEventListener('resize', handleResize)
		return () => window.removeEventListener('resize', handleResize)
	}, [])

	// Reset selection when modal fully closes
	useEffect(() => {
		if (!shouldRender) {
			setSelectedConversation(null)
			setSearchQuery('')
		}
	}, [shouldRender])


	// Handle escape key and global click debugging
	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && isOpen) {
				onClose()
			}
		}


		// Removed click outside behavior - modal only closes via close button or chat bubble click

		document.addEventListener('keydown', handleEscape)

		return () => {
			document.removeEventListener('keydown', handleEscape)
		}
	}, [isOpen, onClose, mode])

	if (!shouldRender) {
		return null
	}


	// Calculate modal dimensions and position based on mode (using tracked windowWidth)
	const fullscreenWidth = windowWidth - 60 // 24px margins on both sides + 12px reduction
	
	// For panel mode, adjust dimensions to avoid chat bubble overlap
	const panelWidth = mode === 'fullscreen' ? fullscreenWidth : 480 // 480px for proper close button visibility

	const modalStyle = {
		position: 'fixed' as const,
		top: isDragging ? '12px' : (isAnimatingOut ? '12px' : (isAnimatingIn ? '24px' : '12px')),
		bottom: mode === 'fullscreen'
			? (isDragging ? '32px' : (isAnimatingOut ? '38px' : (isAnimatingIn ? '32px' : '38px'))) // Animate from 38px to 32px on open
			: (isDragging ? '32px' : (isAnimatingOut ? '38px' : (isAnimatingIn ? '32px' : '38px'))), // Animate from 38px to 32px on open
		// Animate left/right and width for mode transitions (using tracked windowWidth)
		left: mode === 'fullscreen' ? '24px' : `${windowWidth - (modalPosition.right + 18) - panelWidth - 12}px`, // Added 12px buffer
		width: typeof panelWidth === 'number' ? `${panelWidth}px` : panelWidth,
		zIndex: 100,
	}

	return (
		<>
			{/* No backdrop - both modes use content blur instead */}
			
			{/* Modal */}
			<div 
				ref={modalRef}
				data-testid="inbox-modal"
				data-modal-mode={mode}
				data-modal-order-id={orderId}
				onClick={(e) => {
					// Prevent event from bubbling up
					e.stopPropagation()
				}}
				onMouseDown={(e) => {
					// Prevent event from bubbling up
					e.stopPropagation()
				}}
				style={{
					...modalStyle,
					// Animated shadow for focus effect (same for both modes)
					boxShadow: isAnimatingOut 
						? 'rgba(0, 0, 0, 0.6) 0px 0px 20px 0px, rgba(0, 0, 0, 0.6) 0px 0px 5px 0px' // Starting shadow (spread out)
						: (isAnimatingIn 
							? 'rgba(0, 0, 0, 0.6) 0px 0px 10px 0px, rgba(0, 0, 0, 0.6) 0px 0px 5px 0px' // Ending shadow (focused)
							: 'rgba(0, 0, 0, 0.6) 0px 0px 20px 0px, rgba(0, 0, 0, 0.6) 0px 0px 5px 0px'), // Starting shadow
					// Animate opacity and blur for fade + focus effect
					opacity: isAnimatingOut ? 0 : (isAnimatingIn ? 1 : 0),
					filter: isAnimatingOut ? 'blur(4px)' : (isAnimatingIn ? 'blur(0px)' : 'blur(4px)'),
					// Disable transitions during drag for immediate response, and for mode switching
					// Option 2: No left transition - modal stays in place during close
					transition: (isDragging) ? 'none' : 'opacity 300ms ease-out, filter 300ms ease-out, box-shadow 300ms ease-out, top 300ms ease-out, bottom 300ms ease-out'
				}}
				className={cn(
					"bg-background border border-border rounded-lg flex flex-col overflow-hidden",
					// Both modes now use custom animations (no Tailwind animations)
					className
				)}
			>
				{/* Modal Header */}
				<div className="flex items-center justify-between p-4 border-b bg-background/95 backdrop-blur">
					{mode === 'panel' && !showConversationsList && selectedConversation ? (
						/* Panel mode with selected conversation - show back button */
						<div className="flex items-center gap-3">
							<Button 
								variant="ghost" 
								className="h-9 px-3" 
								onClick={() => setShowConversationsList(true)}
							>
								<ArrowLeft className="h-4 w-4 mr-2" />
								Back to Conversations
							</Button>
						</div>
					) : (
						/* Fullscreen mode or conversations list - show title and search */
						<div className="flex items-center gap-4">
							<SectionHeading className="mb-0">Inbox</SectionHeading>
							<div className="relative">
								<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
								<Input
									placeholder="Search conversations..."
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									className="pl-9 w-64 h-9"
								/>
							</div>
						</div>
					)}
					
					<div className="flex items-center gap-2">
						{onToggleMode && (
							<Button variant="ghost" className="h-9 w-9" onClick={onToggleMode}>
								{mode === 'panel' ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
							</Button>
						)}
						<Button 
							variant="ghost" 
							className="h-9 w-9" 
							onClick={() => {
								// If both panels are open, close both; otherwise just close this modal
								if (isOrderPanelOpen && onCloseAll) {
									onCloseAll()
								} else {
									onClose()
								}
							}}
							title={isOrderPanelOpen ? "Close all panels" : "Close inbox"}
						>
							<X className="h-4 w-4" />
						</Button>
					</div>
				</div>

				{/* Modal Content */}
				<div className="flex flex-1 min-h-0">
					{mode === 'fullscreen' ? (
						<>
							{/* Fullscreen: Conversation List - 33% */}
							<div className="w-1/3 border-r border-border flex flex-col">
								<div className="flex-1 overflow-auto p-4">
									<ConversationList
										conversations={conversations}
										selectedConversationId={selectedConversation?.id}
										onSelectConversation={setSelectedConversation}
										searchQuery={searchQuery}
										view={view}
									/>
								</div>
							</div>

							{/* Fullscreen: Conversation Panel - 67% */}
							<div className="flex-1 flex flex-col">
								<ConversationPanel
									conversation={selectedConversation || undefined}
									mode="fullscreen"
									className="h-full"
									shouldAutoFocus={!!orderId}
								/>
							</div>
						</>
					) : (
						/* Panel Mode: Conversations list or single conversation */
						<div className="flex flex-col w-full">
							{showConversationsList ? (
								/* Show conversations list */
								<div className="flex-1 overflow-auto p-4">
									<ConversationList
										conversations={conversations}
										selectedConversationId={selectedConversation?.id}
										onSelectConversation={(conversation) => {
											setSelectedConversation(conversation)
											setShowConversationsList(false)
										}}
										searchQuery={searchQuery}
										view="list"
									/>
								</div>
							) : (
								/* Show single conversation */
								<div className="flex-1">
									<ConversationPanel
										conversation={selectedConversation || undefined}
										mode="halfscreen"
										className="h-full"
										shouldAutoFocus={!!orderId}
									/>
								</div>
							)}
						</div>
					)}
				</div>
			</div>
		</>
	)
}
